import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { formatINR } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { items, subtotal, isOpen, setIsOpen, updateQty, removeItem, keyOf } = useCart();
  const { free_shipping_threshold, allowed_cities } = useSettings();
  const navigate = useNavigate();

  const threshold = free_shipping_threshold || 2000;
  const zonesText = (allowed_cities || ["Delhi NCR"]).join(" / ");
  const freeShippingRemaining = Math.max(0, threshold - subtotal);
  const progress = Math.min(100, (subtotal / threshold) * 100);

  const goToCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#FBF8F3] flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E6DEC3]">
          <SheetTitle className="font-serif text-2xl text-[#1E1B18]">Your Cart</SheetTitle>
          {items.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-[#6E675F] mb-1.5" data-testid="cart-free-shipping-note">
                {freeShippingRemaining > 0
                  ? `Add ${formatINR(freeShippingRemaining)} more for FREE delivery in ${zonesText}`
                  : `You've unlocked FREE delivery in ${zonesText}`}
              </div>
              <div className="h-1.5 rounded-full bg-[#E6DEC3] overflow-hidden">
                <div className="h-full bg-[#9E3B21] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16" data-testid="cart-empty-state">
              <ShoppingBag className="w-14 h-14 text-[#D9822B] mb-4" />
              <p className="font-serif text-xl text-[#1E1B18]">Your jar is empty</p>
              <p className="text-sm text-[#6E675F] mt-2">Add some homemade goodness to get started.</p>
              <Button
                data-testid="cart-empty-shop-btn"
                onClick={() => { setIsOpen(false); navigate("/shop"); }}
                className="btn-primary rounded-full mt-6 px-6"
              >
                Browse Pickles
              </Button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={keyOf(item)}
                  data-testid={`cart-item-${item.product_slug}-${item.line_id || item.weight}`}
                  className="flex gap-3 bg-white rounded-xl p-3 border border-[#E6DEC3]"
                >
                  <img src={item.image_url} alt={item.product_name} className="w-20 h-20 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-serif text-sm sm:text-base font-medium leading-tight text-[#1E1B18]">{item.product_name}</p>
                      <button
                        data-testid={`cart-remove-${item.product_slug}-${item.line_id || item.weight}`}
                        onClick={() => removeItem(item.product_id, item.weight, item.line_id)}
                        className="text-[#6E675F] hover:text-[#9E3B21]"
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-[#6E675F] mt-0.5">{item.weight}</p>
                    {item.is_gift_box && item.gift_note && (
                      <p className="text-xs text-[#5F7355] italic mt-1 line-clamp-2" title={item.gift_note}>
                        Note: "{item.gift_note}"
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 border border-[#E6DEC3] rounded-full">
                        <button
                          data-testid={`cart-qty-dec-${item.product_slug}-${item.line_id || item.weight}`}
                          onClick={() => updateQty(item.product_id, item.weight, item.quantity - 1, item.line_id)}
                          className="p-1.5 hover:bg-[#F4EFE6] rounded-l-full"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-medium w-6 text-center" data-testid={`cart-qty-${item.product_slug}-${item.line_id || item.weight}`}>{item.quantity}</span>
                        <button
                          data-testid={`cart-qty-inc-${item.product_slug}-${item.line_id || item.weight}`}
                          onClick={() => updateQty(item.product_id, item.weight, item.quantity + 1, item.line_id)}
                          className="p-1.5 hover:bg-[#F4EFE6] rounded-r-full"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="font-semibold text-[#1E1B18]">{formatINR(item.unit_price * item.quantity)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E6DEC3] px-6 py-5 bg-white">
            <div className="flex justify-between mb-4">
              <span className="text-sm text-[#6E675F]">Subtotal</span>
              <span className="font-serif text-xl font-semibold" data-testid="cart-subtotal">{formatINR(subtotal)}</span>
            </div>
            <Button
              data-testid="cart-checkout-btn"
              onClick={goToCheckout}
              className="w-full btn-primary rounded-full py-6 text-base font-medium"
            >
              Proceed to Checkout
            </Button>
            <p className="text-xs text-center text-[#6E675F] mt-2">Delivered fresh in {zonesText}</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
